import React, { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import "./MobileAgendaView.css";
import EventDetailsModal from "./EventDetailsModal";

const AGENDA_ZONE = "Europe/London";

function toAgendaDateTime(value) {
  if (!value) return null;
  const parsed = DateTime.fromISO(String(value), { zone: AGENDA_ZONE });
  if (parsed.isValid) return parsed;
  const fallback = DateTime.fromJSDate(new Date(value), { zone: AGENDA_ZONE });
  return fallback.isValid ? fallback : null;
}

function normalizeWeekAnchor(date) {
  const dateTime = DateTime.fromJSDate(new Date(date || new Date()), { zone: AGENDA_ZONE });
  if (!dateTime.isValid) return DateTime.now().setZone(AGENDA_ZONE).startOf("day");
  return dateTime.startOf("day");
}

function startOfWeek(dateTime) {
  const weekdayOffset = dateTime.weekday === 7 ? -6 : 1 - dateTime.weekday;
  return dateTime.plus({ days: weekdayOffset }).startOf("day");
}

function overlapsRange(eventStart, eventEnd, rangeStart, rangeEnd) {
  return eventStart.toMillis() < rangeEnd.toMillis() && eventEnd.toMillis() > rangeStart.toMillis();
}

function isAllDayLikeEvent(event) {
  if (event?.extendedProps?.isHoliday === true) return true;
  if (event?.allDay === true || event?.all_day === true || event?.extendedProps?.allDay === true || event?.extendedProps?.all_day === true) {
    return true;
  }
  const startValue = String(event?.start || "");
  const endValue = String(event?.end || "");
  return startValue.length > 0 && !startValue.includes("T") && (endValue.length === 0 || !endValue.includes("T"));
}

function formatTimeRange(event, timeFormat = "12h") {
  const isAllDay = isAllDayLikeEvent(event);
  if (isAllDay) return "All day";

  const start = toAgendaDateTime(event.start);
  if (!start) return "";
  const end = toAgendaDateTime(event.end);
  const clockFormat = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: timeFormat !== "24h",
    timeZone: AGENDA_ZONE,
  };
  const startLabel = start.toLocaleString(clockFormat);
  if (!end) return startLabel;
  const endLabel = end.toLocaleString(clockFormat);
  return `${startLabel} - ${endLabel}`;
}

function formatRangeLabel(rangeStart, rangeEnd) {
  return `${rangeStart.toFormat("ccc d LLL")} - ${rangeEnd.toFormat("ccc d LLL")}`;
}

function getEventSortKey(event) {
  const start = toAgendaDateTime(event.start);
  return start ? start.toMillis() : Number.MAX_SAFE_INTEGER;
}

function MobileAgendaView({
  events = [],
  selectedDate = null,
  today = new Date(),
  timeFormat = "12h",
  onDayPress = null,
  onEditEvent = null,
  onBackToCalendar = null,
  onWeekAnchorChange = null,
  onCreateEvent = null,
  isDesktop = false,
}) {
  const [anchorDate, setAnchorDate] = useState(() => normalizeWeekAnchor(selectedDate || today));
  const [detailsEvent, setDetailsEvent] = useState(null);
  const lastNavAtRef = useRef(0);
  const touchStartRef = useRef({ x: null, y: null });
  const swipeActiveRef = useRef(false);
  const suppressTapUntilRef = useRef(0);
  const hadGestureMovementRef = useRef(false);
  const suppressNextClickRef = useRef(false);
  const navDoneForTouchRef = useRef(false);
  const lastEmittedAnchorMsRef = useRef(null);
  const initialTodayRef = useRef(normalizeWeekAnchor(today));

  useEffect(() => {
    const next = normalizeWeekAnchor(selectedDate || initialTodayRef.current.toJSDate());
    setAnchorDate((current) => (current.toMillis() === next.toMillis() ? current : next));
  }, [selectedDate, today]);

  useEffect(() => {
    const anchorMs = anchorDate.toMillis();
    if (lastEmittedAnchorMsRef.current === anchorMs) return;
    lastEmittedAnchorMsRef.current = anchorMs;
    onWeekAnchorChange?.(anchorDate.toJSDate());
  }, [anchorDate, onWeekAnchorChange]);

  const { rangeStart, rangeEnd, dayGroups } = useMemo(() => {
    const start = startOfWeek(anchorDate);
    const end = start.plus({ days: 6 });
    const days = Array.from({ length: 7 }, (_, index) => start.plus({ days: index }));

    const groups = days.map((day) => ({ day, items: [] }));
    const sortedEvents = [...events].sort((left, right) => getEventSortKey(left) - getEventSortKey(right));

    sortedEvents.forEach((event) => {
      const startTime = toAgendaDateTime(event.start);
      const endTime = toAgendaDateTime(event.end) || (startTime ? startTime.plus({ minutes: event.all_day ? 24 * 60 : 30 }) : null);
      if (!startTime || !endTime) return;

      groups.forEach((group) => {
        const dayStart = group.day.startOf("day");
        const dayEnd = dayStart.plus({ days: 1 });
        if (overlapsRange(startTime, endTime, dayStart, dayEnd)) {
          group.items.push(event);
        }
      });
    });

    groups.forEach((group) => {
      group.items.sort((left, right) => getEventSortKey(left) - getEventSortKey(right));
    });

    return {
      rangeStart: start,
      rangeEnd: end,
      dayGroups: groups,
    };
  }, [anchorDate, events]);

  const withNavCooldown = (deltaWeeks) => {
    const nowMs = Date.now();
    if (nowMs - lastNavAtRef.current < 460) return;
    lastNavAtRef.current = nowMs;
    setAnchorDate((current) => current.plus({ weeks: deltaWeeks }));
  };

  const handleToday = () => {
    setAnchorDate(normalizeWeekAnchor(today));
  };

  const handleTouchStart = (event) => {
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest("[data-swipe-ignore='true'], input, select, textarea")
    ) {
      swipeActiveRef.current = false;
      return;
    }

    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    swipeActiveRef.current = true;
    hadGestureMovementRef.current = false;
    suppressNextClickRef.current = false;
    navDoneForTouchRef.current = false;
  };

  const handleTouchMove = (event) => {
    if (!swipeActiveRef.current) return;
    const startX = touchStartRef.current.x;
    const startY = touchStartRef.current.y;
    const touch = event.touches?.[0];
    if (!touch || startX == null || startY == null) return;

    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absX > 10 || absY > 10) {
      hadGestureMovementRef.current = true;
      suppressNextClickRef.current = true;
    }
  };

  const handleTouchEnd = (event) => {
    const startX = touchStartRef.current.x;
    const startY = touchStartRef.current.y;
    touchStartRef.current = { x: null, y: null };
    if (!swipeActiveRef.current) return;
    swipeActiveRef.current = false;

    const touch = event.changedTouches?.[0];
    if (!touch || startX == null || startY == null) return;

    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absX > 14 || absY > 14) {
      suppressNextClickRef.current = true;
      suppressTapUntilRef.current = Date.now() + 900;
    }

    if (navDoneForTouchRef.current) return;
    if (absX > 60 && absX > absY * 1.35) {
      navDoneForTouchRef.current = true;
      if (deltaX < 0) {
        withNavCooldown(1);
      } else {
        withNavCooldown(-1);
      }
    }
  };

  const handleTouchCancel = () => {
    touchStartRef.current = { x: null, y: null };
    swipeActiveRef.current = false;
    navDoneForTouchRef.current = false;
    hadGestureMovementRef.current = false;
  };

  const visibleDayGroups = dayGroups.filter((group) => group.items.length > 0);

  const handleClickCapture = (event) => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (!hadGestureMovementRef.current) return;
    if (Date.now() >= suppressTapUntilRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <section className="mobile-agenda-view" aria-label="Agenda view">
      <div className="mobile-agenda-header" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onTouchCancel={handleTouchCancel}>
        {isDesktop ? (
          <button type="button" className="mobile-agenda-nav-btn" data-swipe-ignore="true" onClick={() => withNavCooldown(-1)} aria-label="Previous week">&#8249;</button>
        ) : (
          <div className="mobile-agenda-spacer" aria-hidden="true" />
        )}
        <div className="mobile-agenda-heading">
          <div className="mobile-agenda-title">Agenda</div>
          <div className="mobile-agenda-range">{formatRangeLabel(rangeStart, rangeEnd)}</div>
        </div>
        {isDesktop ? (
          <button type="button" className="mobile-agenda-nav-btn" data-swipe-ignore="true" onClick={() => withNavCooldown(1)} aria-label="Next week">&#8250;</button>
        ) : (
          <button type="button" className="mobile-agenda-create" data-swipe-ignore="true" onClick={() => onCreateEvent?.(anchorDate.toJSDate())} aria-label="Create new event">
            +
          </button>
        )}
      </div>

      <div className="mobile-agenda-body" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onTouchCancel={handleTouchCancel} onClickCapture={handleClickCapture}>
        <div className="mobile-agenda-top-controls">
          <button type="button" className="mobile-agenda-today" data-swipe-ignore="true" onClick={handleToday}>
            Today
          </button>
          {onBackToCalendar ? (
            <button type="button" className="mobile-agenda-calendar" data-swipe-ignore="true" onClick={onBackToCalendar} aria-label="Back to calendar">
              Calendar
            </button>
          ) : null}
        </div>

        {visibleDayGroups.length === 0 ? (
          <div className="mobile-agenda-empty-week">No events in this week.</div>
        ) : null}

        {visibleDayGroups.map((group) => {
          const day = group.day;
          const items = group.items || [];
          const isToday = day.hasSame(DateTime.now().setZone(AGENDA_ZONE), "day");

          return (
            <section key={day.toISODate()} className={`mobile-agenda-day ${isToday ? "is-today" : ""}`.trim()}>
              <button
                type="button"
                className="mobile-agenda-day-header"
                onClick={() => {
                  onDayPress?.(day.toJSDate());
                }}
              >
                <span className="mobile-agenda-day-name">{day.toFormat("ccc d LLL")}</span>
                {isToday ? <span className="mobile-agenda-day-tag">Today</span> : null}
              </button>

              {items.length > 0 ? (
                <div className="mobile-agenda-items" role="list" aria-label={`${day.toFormat("ccc d LLL")} events`}>
                  {items.map((event) => {
                    const start = toAgendaDateTime(event.start);
                    const title = String(event.title || "Untitled event");
                    const location = String(event.extendedProps?.location || "").trim();
                    const project = String(event.extendedProps?.project_name || "").trim();
                    const category = String(event.extendedProps?.category_name || "").trim();
                    const categoryColor = String(event.extendedProps?.category_color || "").trim();
                    const hasValidCategoryColor = /^#[0-9A-Fa-f]{6}$/.test(categoryColor);
                    
                    const metaWithoutCategory = [location, project].filter(Boolean).join(" · ");

                    return (
                      <button
                        key={`${day.toISODate()}-${event.id || title}-${start ? start.toMillis() : "na"}`}
                        type="button"
                        className={`mobile-agenda-item ${event.extendedProps?.isHoliday === true ? "is-holiday" : ""}`.trim()}
                        role="listitem"
                        onClick={() => {
                          if (Date.now() < suppressTapUntilRef.current) return;
                          setDetailsEvent({
                            ...event,
                            extendedProps: {
                              ...(event?.extendedProps || {}),
                            },
                          });
                        }}
                      >
                        <div className="mobile-agenda-item-time">{formatTimeRange(event, timeFormat)}</div>
                        <div className="mobile-agenda-item-main">
                          <div className="mobile-agenda-item-title">{title}</div>
                          <div className="mobile-agenda-item-meta-wrapper">
                            {metaWithoutCategory ? <span className="mobile-agenda-item-meta">{metaWithoutCategory}</span> : null}
                            {category ? (
                              <span
                                className="mobile-agenda-item-category-chip"
                                style={
                                  hasValidCategoryColor
                                    ? { backgroundColor: categoryColor }
                                    : {}
                                }
                              >
                                {category}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>

      <EventDetailsModal
        event={detailsEvent}
        onClose={() => setDetailsEvent(null)}
        onEdit={() => {
          if (!detailsEvent) return;
          if (typeof onEditEvent === "function") {
            onEditEvent(detailsEvent);
            setDetailsEvent(null);
            return;
          }
          const start = toAgendaDateTime(detailsEvent.start) || anchorDate;
          setDetailsEvent(null);
          onDayPress?.(start.toJSDate());
        }}
        onDelete={() => setDetailsEvent(null)}
        timeFormat={timeFormat}
      />
    </section>
  );
}

export default MobileAgendaView;